import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export const Pricing = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);

  const handleSubscribe = async (planName: string) => {
    try {
      setLoading(planName);

      // Check if user is authenticated
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to subscribe to a plan.",
          variant: "destructive",
        });
        return;
      }

      // STRICT: Only allow subscription to verified plans
      if (planName.toLowerCase() === "starter") {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { plan: planName.toLowerCase() },
        });

        if (error) throw error;

        // Open Stripe checkout in a new tab
        window.open(data.url, "_blank");

        toast({
          title: "Redirecting to Stripe Checkout",
          description: "Complete your subscription to unlock Starter Plan features.",
        });
      } else if (planName.toLowerCase() === "enterprise") {
        // Handle enterprise contact form
        toast({
          title: "Enterprise Plan",
          description: "Please contact our sales team for enterprise pricing.",
        });
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for individual landlords just getting started",
      features: [
        "Up to 3 properties",
        "Up to 3 tenants per property",
        "Basic rent tracking",
        "Tenant management",
        "Maintenance requests",
        "Email support",
      ],
      notIncluded: [
        "Unlimited properties",
        "Financial reporting & accounting",
        "Lease signing documents",
        "Recurring payments",
        "Priority support",
      ],
      cta: "Get Started",
      popular: false,
      buttonStyle: "outline",
    },
    {
      name: "Starter",
      price: "$29",
      period: "per month",
      description: "Ideal for growing property management businesses",
      features: [
        "Up to 15 properties",
        "Up to 5 tenants per property",
        "Complete rent tracking",
        "Financial reporting & accounting",
        "Lease signing documents",
        "Maintenance tracking",
        "Recurring payments",
        "Booking management",
        "Reminders & notifications",
        "Priority email support",
      ],
      notIncluded: [
        "Unlimited properties & tenants",
        "Bulk property import",
        "Priority phone support",
        "Dedicated account manager",
      ],
      cta: "Subscribe Now",
      popular: true,
      buttonStyle: "default",
      note: "⚠️ No trial access - requires active Stripe subscription",
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large-scale property management companies",
      features: [
        "Unlimited properties",
        "Unlimited tenants per property",
        "All Starter features",
        "Bulk property import",
        "Priority phone support",
        "Dedicated account manager",
        "Custom onboarding",
        "Volume discounts",
        "SLA guarantee",
      ],
      notIncluded: [],
      cta: "Contact Sales",
      popular: false,
      buttonStyle: "outline",
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">Choose Your Plan</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Start free and upgrade as you grow. All plans include our core features with no setup fees.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-card rounded-2xl shadow-lg p-8 border border-border ${
                  plan.popular ? "ring-2 ring-primary scale-105" : "hover:shadow-xl"
                } transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-card-foreground mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-card-foreground">{plan.price}</span>
                    <span className="text-muted-foreground ml-2">/{plan.period}</span>
                  </div>
                  <p className="text-muted-foreground">{plan.description}</p>
                </div>

                <div className="mb-8">
                  <div className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <Check className="h-5 w-5 text-accent mr-3 flex-shrink-0" />
                        <span className="text-card-foreground">{feature}</span>
                      </div>
                    ))}
                    {plan.notIncluded.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <X className="h-5 w-5 text-muted-foreground/70 mr-3 flex-shrink-0" aria-hidden="true" />
                        <span className="text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {plan.name === "Free" ? (
                  <Link to="/signup">
                    <Button variant="outline" className="w-full py-3" size="lg">
                      {plan.cta}
                    </Button>
                  </Link>
                ) : plan.name === "Enterprise" ? (
                  <Button
                    variant="outline"
                    className="w-full py-3"
                    size="lg"
                    onClick={() => window.open("mailto:sales@rentflow.com", "_blank")}
                  >
                    {plan.cta}
                  </Button>
                ) : (
                  <Link to={`/signup?plan=${plan.name.toLowerCase()}&trial=true`}>
                    <Button className="w-full py-3" size="lg">
                      {plan.cta}
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-muted-foreground">Freemium. No credit card required.</p>
          </div>
        </div>
      </div>
    </section>
  );
};
